/**
 * The Generate flow in the Pictures side panel (spec 027, Flow steps 1–6):
 * before a job, the Brief field (typed, or "Write from the story", saved to
 * the map with `notePictureSlot`), the character's Framing and Generate,
 * which stays disabled without a brief (D-22, D-23); then drafts appear as
 * they finish (on a checkerboard for characters and props, D-24; a full
 * picture's and a background's are opaque, D-36), a failed
 * one has its own Retry, "4 more" (or "Regenerate with new brief" once the
 * brief is edited), a pick shows the library name and where it goes, and
 * "Accept and place" files it and places it in one story history entry.
 * Closing the panel keeps the job. The Brief field shows the slot's current
 * brief until the author edits it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CharacterFraming, PictureSlot, StoryApiResponse } from '../../../../Shared/src/@types';
import { BRIEF_MAX, DEFAULT_DRAFT_COUNT, canvasOf, cutLabel, defaultLibraryName, isCutOutKind, pictureBoxOf } from '../../../../Shared/src/images';
import { FRAMING_LABELS, framingOf, pictureSlotLabel } from '../../../../Shared/src/pictures';
import { hasCharacterPictures, masterSlotOf, resetTargetsOf } from '../../../../Shared/src/storyOperations';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { useImageGenStore } from '../../stores/imageGenStore';
import { useStoryStore } from '../../stores/storyStore';
import { usePictureScenes } from '../pictures/usePictureScenes';
import { usePictureOwner } from '../pictures/pictureOwner';
import FramingControl from './FramingControl';
import { SlotGenerateButton } from './GenerateButton';
import { BRIEF_TEXT, briefTextFor, IMAGE_TEXT, REASON_TEXT, writeBriefTooltip } from './imageGenText';
import { placeChoices, useKey } from './imageGenModel';
import styles from './ImageGen.module.scss';

export interface GenerateFlowProps {
  slot: PictureSlot;
  /** A story owner's story (027); a scene owner (spec 030) reads its one scene from the owner. */
  story?: Pick<StoryApiResponse, 'graph' | 'storyScenes'>;
}

const EMPTY_STORY: Pick<StoryApiResponse, 'graph' | 'storyScenes'> = { graph: { nodes: [], edges: [], startNodeId: '' }, storyScenes: [] };

/** An expression's details (D-33): the framing, and a link that selects the master picture, where it is chosen. */
const MasterFramingLink: React.FC<{ master: PictureSlot; framing: CharacterFraming }> = ({ master, framing }) => {
  const [, setParams] = useSearchParams();
  return (
    <div className={styles.framing} data-testid="generate-framing-master">
      <span className={styles.fieldLabel}>{BRIEF_TEXT.framing}</span>
      <p className={styles.muted}>
        {BRIEF_TEXT.framingOnMaster(FRAMING_LABELS[framing])}{' '}
        <button
          type="button"
          className={styles.link}
          onClick={() => setParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('view', 'pictures');
            next.set('slot', master.id);
            return next;
          }, { replace: true })}
          data-testid="generate-framing-master-link"
        >
          {BRIEF_TEXT.framingMasterLink(pictureSlotLabel(master))}
        </button>
      </p>
    </div>
  );
};

export const GenerateFlow: React.FC<GenerateFlowProps> = ({ slot, story }) => {
  const entry = useImageGenStore((s) => s.entries[slot.id]);
  const settings = useImageGenStore((s) => s.settings);
  const limitNotice = useImageGenStore((s) => s.limitNotice);
  const { cancel, retry, more, pick, accept, undoPlacement, redraw } = useImageGenStore.getState();
  // The story's (027) or, spec 030, the standalone scene's own map, scenes and history.
  const owner = usePictureOwner();
  const isScene = owner.ref.kind === 'scene';
  const storyScenes = usePictureScenes(isScene ? EMPTY_STORY : story ?? EMPTY_STORY);
  const scenes = isScene ? owner.scenes : storyScenes;
  const choices = useMemo(() => placeChoices(slot, scenes), [slot, scenes]);
  const T = briefTextFor(owner.noun);

  const storyMap = useStoryStore((s) => s.pictureMap);
  const storyApply = useStoryStore((s) => s.applyOperations);
  const map = isScene ? owner.map : storyMap;
  const applyOperations = isScene ? owner.apply : storyApply;
  // The author's edit of the Brief field; null while it shows the slot's brief (D-23).
  const [edit, setEdit] = useState<string | null>(null);
  const [briefError, setBriefError] = useState<string | undefined>();
  const [writing, setWriting] = useState(false);
  const [name, setName] = useState(defaultLibraryName(slot));
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // A new job or another slot: the field follows the slot's brief again, and every place is ticked.
  const jobId = entry?.jobId;
  useEffect(() => { setEdit(null); setBriefError(undefined); }, [jobId, slot.id]);
  useEffect(() => { setTicked(new Set(choices.map((c) => useKey(c.use)))); }, [jobId, choices.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setName(defaultLibraryName(slot)); }, [slot.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saved = (slot.brief ?? '').trim();
  const brief = edit ?? (slot.brief || entry?.brief || '');
  const isCharacter = slot.kind === 'character';
  const framing = framingOf(map, slot.subject);
  // D-33: framing is chosen on the character's master picture only.
  const master = isCharacter ? masterSlotOf(map, slot.subject) : undefined;
  const isMaster = !!master && master.id === slot.id;
  const entries = useImageGenStore((s) => s.entries);
  const targets = useMemo(() => (isCharacter ? resetTargetsOf(map, slot.id).map((t) => t.id) : []), [map, slot.id, isCharacter]);
  const hasRun = targets.some((id) => entries[id] && entries[id].status !== 'cancelled' && entries[id].status !== 'interrupted');
  const framingResets = isCharacter && (hasCharacterPictures(map, slot.subject) || hasRun);
  const [pendingFraming, setPendingFraming] = useState<CharacterFraming | null>(null);

  // One save at a time: leaving the field and pressing Generate share it.
  const pending = useRef<{ text: string; done: Promise<boolean> } | null>(null);

  /** Saves the brief to the map (one `notePictureSlot` step) when it differs from the slot's. */
  const saveBrief = (text: string): Promise<boolean> => {
    const t = text.trim();
    if (pending.current && pending.current.text === t) return pending.current.done;
    const done = doSave(t).finally(() => { if (pending.current?.done === done) pending.current = null; });
    pending.current = { text: t, done };
    return done;
  };
  const doSave = async (t: string): Promise<boolean> => {
    if (t === saved) { setEdit(null); return true; }
    if (!t) return true; // an emptied field is not saved; Generate stays disabled
    const res = await applyOperations([{
      name: 'notePictureSlot',
      args: { kind: slot.kind, subject: slot.subject, ...(slot.kind === 'background' && !slot.variant ? {} : { variant: slot.variant }), brief: t, why: slot.why ?? '' },
      createdIds: [],
    }]);
    if (!res.ok) { setBriefError(T.saveFailed); return false; }
    setBriefError(undefined);
    setEdit(null);
    return true;
  };
  const saveIfEdited = async (): Promise<boolean> => (edit === null ? true : saveBrief(edit));

  const writeFromStory = async () => {
    setWriting(true);
    setBriefError(undefined);
    const res = await useImageGenStore.getState().writeBrief(slot.id);
    setWriting(false);
    if (res.ok === false) { setBriefError(res.error); return; }
    setEdit(res.brief);
    await saveBrief(res.brief);
  };

  const setFraming = (next: CharacterFraming) => {
    if (framingResets) { setPendingFraming(next); return; }
    void applyOperations([{ name: 'setPictureFraming', args: { subject: slot.subject, framing: next }, createdIds: [] }]);
  };
  /** The confirmed change (D-33): the reset and the new framing as one `resetPictureSlot` step (one Undo). */
  const resetWithFraming = async () => {
    if (!pendingFraming) return;
    const res = await applyOperations([{ name: 'resetPictureSlot', args: { slot: slot.id, framing: pendingFraming }, createdIds: [] }]);
    if (!res.ok) throw new Error(T.framingFailed);
    useImageGenStore.getState().forget(targets);
  };

  const writeTip = settings ? writeBriefTooltip(settings) : { lines: [], disabled: true };
  const briefField = (
    <>
      <label className={styles.field}>
        {T.label}
        <textarea
          value={brief}
          maxLength={BRIEF_MAX}
          onChange={(e) => setEdit(e.target.value)}
          onBlur={() => { void saveIfEdited(); }}
          data-testid="generate-brief"
        />
      </label>
      <div className={styles.briefRow}>
        <button
          type="button"
          className={styles.button}
          disabled={writing || writeTip.disabled}
          title={writeTip.lines.join('\n')}
          data-tooltip={writeTip.lines.join('\n')}
          onClick={() => { void writeFromStory(); }}
          data-testid="generate-write-brief"
        >
          {T.write}
        </button>
        {writing && <span className={styles.muted} data-testid="generate-writing-brief"><span className={styles.spinner} /> {T.writing}</span>}
      </div>
      {briefError && <p className={styles.error} role="alert" data-testid="generate-brief-error">{briefError}</p>}
    </>
  );
  const framingField = !isCharacter ? null : isMaster ? (
    <>
      <FramingControl subject={slot.subject} value={framing} onChange={setFraming} resets={framingResets} />
      <ConfirmDeleteModal
        show={pendingFraming !== null}
        title={T.framingConfirmTitle(slot.subject)}
        actions={[{ key: 'reset', label: T.framingConfirm, variant: 'danger', isDefault: true, run: resetWithFraming }]}
        onClose={() => setPendingFraming(null)}
      >
        <p className="mb-1" data-testid="framing-confirm-text">
          {T.framingConfirmText(slot.subject, pendingFraming ? FRAMING_LABELS[pendingFraming] : '')}
        </p>
        <p className="mb-0">{T.framingConfirmKeeps}</p>
      </ConfirmDeleteModal>
    </>
  ) : master ? <MasterFramingLink master={master} framing={framing} /> : null;
  const nextRound = (
    <>
      {briefField}
      {framingField}
      <SlotGenerateButton slot={slot} where="panel" brief={brief} framing={isCharacter ? framing : undefined} beforeStart={edit === null ? undefined : saveIfEdited} />
      {!brief.trim() && <p className={styles.muted} data-testid="generate-needs-brief">{T.needsBrief}</p>}
    </>
  );

  const live = entry && entry.status !== 'cancelled' && entry.status !== 'interrupted';

  // Every slot with uses offers generation (D-21): Generate when needed, Generate new when filled.
  const offers = slot.uses.length > 0;
  if (!live) {
    if (!offers && !entry) return null;
    return (
      <div className={styles.flow} data-testid="generate-flow">
        {entry?.status === 'interrupted' && <p className={styles.error} data-testid="generate-interrupted">{IMAGE_TEXT.interrupted}</p>}
        {limitNotice && <p className={styles.error} role="alert" data-testid="generate-limit">{limitNotice}</p>}
        {entry?.error && !limitNotice && <p className={styles.error} role="alert">{entry.error}</p>}
        {offers && nextRound}
      </div>
    );
  }

  const drafts = entry.drafts;
  const readyCount = drafts.filter((d) => d.status === 'ready').length;
  const running = entry.status === 'running';
  const briefEdited = brief.trim() !== (entry.brief || slot.brief).trim() && brief.trim().length > 0;
  const cutOut = isCutOutKind(slot.kind);
  const checker = cutOut ? styles.checker : '';
  const count = DEFAULT_DRAFT_COUNT;
  const picked = entry.pick !== undefined ? drafts.find((d) => d.index === entry.pick && d.status === 'ready') : undefined;
  // The tile takes the drafts' own shape (D-28), so a portrait draft is never cropped.
  const canvas = entry.canvas ?? canvasOf(slot.kind, slot.framing, slot.kind === 'other' ? pictureBoxOf(scenes, slot.subject) : undefined);
  const shape = canvas === 'wide' ? styles.draftWide : canvas === 'portrait' ? styles.draftTall : '';

  const onAccept = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await accept(slot.id, {
        name: name.trim() || defaultLibraryName(slot),
        uses: choices.filter((c) => ticked.has(useKey(c.use))).map((c) => c.use),
        kind: slot.kind,
        subject: slot.subject,
      });
    } finally {
      setBusy(false);
    }
  };

  if (entry.accepted) {
    const final = entry.accepted.final;
    return (
      <div className={styles.flow} data-testid="generate-flow" data-state="accepted">
        {final === 'drawing' && <p className={styles.status} data-testid="generate-drawing-full"><span className={styles.spinner} /> {IMAGE_TEXT.drawingFull}</p>}
        {final === 'failed' && (
          <p className={styles.error} data-testid="generate-full-failed">
            {IMAGE_TEXT.fullFailed}{' '}
            <button type="button" className={styles.link} onClick={() => { void redraw(slot.id); }} data-testid="generate-redraw">{IMAGE_TEXT.retry}</button>
          </p>
        )}
        {entry.placed && (
          <p className={styles.status} role="status" data-testid="generate-placed">
            <span>{entry.placed}</span>
            <button type="button" className={styles.link} onClick={() => { void undoPlacement(slot.id); }} data-testid="generate-undo">{IMAGE_TEXT.undo}</button>
          </p>
        )}
        {entry.error && <p className={styles.error} role="alert">{entry.error}</p>}
        {/* Another round once the full size has landed (or failed), without a reload. */}
        {final !== 'drawing' && offers && nextRound}
      </div>
    );
  }

  return (
    <div className={styles.flow} data-testid="generate-flow" data-state={running ? 'running' : 'ready'}>
      <p className={styles.note}>{IMAGE_TEXT.drawing(drafts.length)}</p>
      <p className={styles.muted}>{IMAGE_TEXT.drawingNote}</p>
      <div className={styles.row}>
        <span className={styles.progress} data-testid="generate-progress">
          {running && <span className={styles.spinner} />}
          {IMAGE_TEXT.ready(readyCount, drafts.length)}
        </span>
        {running && (
          <button type="button" className={styles.button} onClick={() => { void cancel(slot.id); }} data-testid="generate-cancel">
            {IMAGE_TEXT.cancel}
          </button>
        )}
      </div>
      <div className={styles.drafts}>
        {drafts.map((d) => {
          if (d.status === 'failed') {
            return (
              <div key={d.index} className={`${styles.draft} ${shape} ${styles.draftFailed}`} data-testid="generate-draft" data-status="failed">
                <span data-testid="generate-draft-failed">{IMAGE_TEXT.draftFailed(d.index)}</span>
                {d.reason && REASON_TEXT[d.reason] && <span className={styles.muted} data-testid="generate-draft-reason">{REASON_TEXT[d.reason]}</span>}
                <button type="button" className={styles.link} onClick={() => { void retry(slot.id, d.index); }} data-testid="generate-draft-retry">
                  {IMAGE_TEXT.retry}
                </button>
              </div>
            );
          }
          const url = entry.draftUrls[d.index];
          const isPick = entry.pick === d.index;
          return (
            <button
              key={d.index}
              type="button"
              className={`${styles.draft} ${shape} ${isPick ? styles.draftPicked : ''} ${checker}`}
              data-checker={cutOut ? 'true' : undefined}
              disabled={d.status !== 'ready'}
              aria-pressed={isPick}
              aria-label={`Draft ${d.index}`}
              onClick={() => pick(slot.id, isPick ? undefined : d.index)}
              data-testid="generate-draft"
              data-status={d.status}
              data-canvas={canvas}
              data-cut={d.status === 'ready' && d.cut ? 'true' : undefined}
            >
              {d.status === 'ready' && url ? <img src={url} alt={`Draft ${d.index}`} /> : <span className={styles.spinner} />}
              {/* A cut draft stays pickable; the marker only warns (D-29). */}
              {d.status === 'ready' && d.cut && (
                <span className={styles.cutMark} data-testid="generate-draft-cut">{cutLabel(d.cutEdges ?? ['top'])}</span>
              )}
            </button>
          );
        })}
      </div>
      {drafts.some((d) => d.status === 'failed') && <p className={styles.muted}>{IMAGE_TEXT.failedCounts}</p>}
      {(entry.error || limitNotice) && <p className={styles.error} role="alert" data-testid="generate-error">{limitNotice ?? entry.error}</p>}
      <label className={styles.field}>
        {T.label}
        <textarea
          value={brief}
          maxLength={BRIEF_MAX}
          onChange={(e) => setEdit(e.target.value)}
          onBlur={() => { void saveIfEdited(); }}
          data-testid="generate-brief"
        />
      </label>
      {briefError && <p className={styles.error} role="alert" data-testid="generate-brief-error">{briefError}</p>}
      <div className={styles.row}>
        <button
          type="button"
          className={styles.button}
          disabled={!settings || settings.limit.left < count}
          onClick={() => {
            void (async () => {
              if (briefEdited && !(await saveIfEdited())) return;
              await more(slot.id, briefEdited ? brief.trim() : undefined);
            })();
          }}
          data-testid="generate-more"
        >
          {briefEdited ? IMAGE_TEXT.regenerate : IMAGE_TEXT.more(count)}
        </button>
      </div>
      {picked && (
        <>
          <div className={`${styles.enlarged} ${checker}`} data-testid="generate-enlarged" data-checker={cutOut ? 'true' : undefined}>
            {entry.draftUrls[picked.index] && <img src={entry.draftUrls[picked.index]} alt={`Draft ${picked.index}, enlarged`} />}
          </div>
          <label className={styles.field}>
            {IMAGE_TEXT.libraryName}
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} data-testid="generate-name" />
          </label>
          <fieldset className={styles.checks} data-testid="generate-places">
            <legend className={styles.muted}>{IMAGE_TEXT.placeIn}</legend>
            {choices.map((c) => {
              const k = useKey(c.use);
              return (
                <label key={k}>
                  <input
                    type="checkbox"
                    checked={ticked.has(k)}
                    onChange={(e) => setTicked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(k);
                      else next.delete(k);
                      return next;
                    })}
                    data-testid="generate-place"
                  />{' '}
                  {c.label}
                </label>
              );
            })}
          </fieldset>
        </>
      )}
      <button
        type="button"
        className={styles.primary}
        disabled={!picked || busy}
        onClick={() => { void onAccept(); }}
        data-testid="generate-accept"
      >
        {IMAGE_TEXT.accept}
      </button>
    </div>
  );
};

export default GenerateFlow;
